import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Plus, AlertTriangle, ChevronRight, Check } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { cardClass, primaryText, secondaryText, semiBoldText, smallTextSize } from '../../../styling/stylingHelper';
import {
  AdminSetupUser,
  SetupProfile,
  SetupPlatform,
  getAdminSetupUsers,
  getAdminSetupProfile,
  createSetupPlatform,
  createSetupSection,
  updateSetupPlatform,
} from '../../../services/admin/adminSetupService';
import { Platform } from '../../../types/Platforms';
import { ChipListEditor } from './AdminSetupPage';

const PLATFORM_OPTIONS = Object.values(Platform).filter(v => typeof v === 'string') as string[];
const NEW_PLATFORM = '__new__';

type Step = 'client' | 'restaurant' | 'section' | 'platform' | 'edit';

// Modal to add/update a restaurant_platforms match from the AI Checker email list — the main fix for
// "should have been processed" emails whose sender/client-name isn't set up yet. A stepped flow
// (client → restaurant → section → platform) with a clickable breadcrumb; single options are
// auto-selected, and when the audited email's platform already exists under the chosen section the
// flow jumps straight into editing it. The platform enum is pre-seeded from the audited email when
// known; the sender address is only shown as context (see the seeding effect for why).
export function AddRestaurantPlatformModal({ initialPlatform, initialOrderEmail, onClose }: {
  initialPlatform?: string | null;
  initialOrderEmail?: string | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();

  const [users, setUsers] = useState<AdminSetupUser[] | null>(null);
  const [userQuery, setUserQuery] = useState('');
  const [userId, setUserId] = useState('');
  const [profile, setProfile] = useState<SetupProfile | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [restaurantId, setRestaurantId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [newSectionType, setNewSectionType] = useState<'FOOD' | 'DRINKS' | 'NON_FOOD'>('FOOD');
  const [newSectionName, setNewSectionName] = useState('');
  const [creatingSection, setCreatingSection] = useState(false);
  // Existing platform id, or NEW_PLATFORM to create one under the section.
  const [platformChoice, setPlatformChoice] = useState('');
  const [newPlatformName, setNewPlatformName] = useState(
    initialPlatform && PLATFORM_OPTIONS.includes(initialPlatform) ? initialPlatform : '',
  );
  const [orderEmails, setOrderEmails] = useState<string[]>([]);
  const [orderClientNames, setOrderClientNames] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setUsers(await getAdminSetupUsers());
      } catch (err: any) {
        setError(err.message || 'Failed to load clients');
        setUsers([]);
      }
    })();
  }, []);

  const filteredUsers = useMemo(() => {
    if (!users) return [];
    const q = userQuery.trim().toLowerCase();
    if (!q) return users;
    return users.filter(u => u.email.toLowerCase().includes(q) || (u.name && u.name.toLowerCase().includes(q)));
  }, [users, userQuery]);

  const user = users?.find(u => u.id === userId) ?? null;
  const restaurant = profile?.restaurants.find(r => r.id === restaurantId) ?? null;
  const section = restaurant?.sections.find(s => s.id === sectionId) ?? null;
  const existingPlatform = section?.platforms.find(p => p.id === platformChoice) ?? null;

  const step: Step = !userId ? 'client' : !restaurantId ? 'restaurant' : !sectionId ? 'section' : !platformChoice ? 'platform' : 'edit';

  const selectUser = async (id: string) => {
    setUserId(id);
    setProfile(null);
    setRestaurantId('');
    setSectionId('');
    setPlatformChoice('');
    setDone(false);
    setError(null);
    if (!id) return;
    setLoadingProfile(true);
    try {
      const p = await getAdminSetupProfile(id);
      setProfile(p);
      // Auto-select when there is only one restaurant (the common case).
      if (p.restaurants.length === 1) selectRestaurant(p.restaurants[0].id, p);
    } catch (err: any) {
      setError(err.message || 'Failed to load profile');
    } finally {
      setLoadingProfile(false);
    }
  };

  const selectRestaurant = (id: string, p: SetupProfile | null = profile) => {
    setRestaurantId(id);
    setPlatformChoice('');
    setDone(false);
    const r = p?.restaurants.find(x => x.id === id);
    if (r && r.sections.length === 1) selectSection(r.sections[0].id, r.sections[0]);
    else setSectionId('');
  };

  const selectSection = (id: string, sec = restaurant?.sections.find(s => s.id === id)) => {
    setSectionId(id);
    setDone(false);
    // If the audited email's platform already exists under this section, jump straight to editing it —
    // the usual fix is adding the missing order email / client name to an existing platform.
    const match = initialPlatform ? sec?.platforms.find(p => p.platformName === initialPlatform) : null;
    setPlatformChoice(match ? match.id : '');
  };

  const addSection = async () => {
    if (!restaurant || creatingSection) return;
    setCreatingSection(true); setError(null);
    try {
      const created = await createSetupSection({ restaurantId: restaurant.id,
        name: newSectionName.trim() || t(`accountSetup.suppliers.sectionType.${newSectionType}`), restaurantSectionType: newSectionType });
      const updated = await getAdminSetupProfile(userId);
      setProfile(updated);
      const saved = updated.restaurants.find(item => item.id === restaurant.id)?.sections.find(item => item.id === created.id);
      if (!saved) throw new Error(t('accountSetup.error.generic'));
      setNewSectionName('');
      selectSection(saved.id, saved);
    } catch (failure) {
      console.error('Failed to create restaurant section', failure);
      const code = failure instanceof Error ? failure.message : '';
      setError(t(`accountSetup.suppliers.errors.${code}`, { defaultValue: t('accountSetup.error.generic') }));
    } finally { setCreatingSection(false); }
  };

  // Breadcrumb back-navigation: clicking a crumb clears that selection and everything after it.
  const goBackTo = (target: Step) => {
    if (creatingSection) return;
    setDone(false);
    setError(null);
    if (target === 'client') { setUserId(''); setProfile(null); setRestaurantId(''); setSectionId(''); setPlatformChoice(''); }
    if (target === 'restaurant') { setRestaurantId(''); setSectionId(''); setPlatformChoice(''); }
    if (target === 'section') { setSectionId(''); setPlatformChoice(''); }
    if (target === 'platform') setPlatformChoice('');
  };

  // Seed the chip editors whenever the platform target changes: existing platform → its current values,
  // new platform → empty. The audited email's sender is deliberately NOT pre-added: it is usually the
  // forwarding address, not the supplier's own order address, so it would be wrong more often than right.
  useEffect(() => {
    if (!platformChoice) {
      setOrderEmails([]);
      setOrderClientNames([]);
      return;
    }
    if (platformChoice === NEW_PLATFORM) {
      setOrderEmails([]);
      setOrderClientNames([]);
      return;
    }
    setOrderEmails(existingPlatform?.orderEmails ?? []);
    setOrderClientNames(existingPlatform?.orderClientNames ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platformChoice, sectionId]);

  const canSave = step === 'edit' && !saving
    && (platformChoice === NEW_PLATFORM ? !!newPlatformName : !!platformChoice);

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    setDone(false);
    try {
      if (platformChoice === NEW_PLATFORM) {
        await createSetupPlatform({
          restaurantSectionId: sectionId,
          platformName: newPlatformName,
          orderEmails: orderEmails.length > 0 ? orderEmails : undefined,
          orderClientNames: orderClientNames.length > 0 ? orderClientNames : undefined,
        });
      } else {
        await updateSetupPlatform(platformChoice, { orderEmails, orderClientNames });
      }
      setDone(true);
      // Refresh the profile so a subsequent edit in the same modal starts from the saved state.
      const p = await getAdminSetupProfile(userId);
      setProfile(p);
      if (platformChoice === NEW_PLATFORM) {
        const savedSection = p.restaurants.find(r => r.id === restaurantId)?.sections.find(s => s.id === sectionId);
        const created = savedSection?.platforms.find(pl => pl.platformName === newPlatformName);
        if (created) setPlatformChoice(created.id);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // ── UI helpers ──────────────────────────────────────────────────────────────
  const rowCls = cn(
    'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border text-left transition-colors',
    'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50',
    'hover:border-purple-300 dark:hover:border-purple-700 hover:bg-purple-50/50 dark:hover:bg-purple-900/10',
  );
  const inputCls = cn(
    'w-full px-3 py-2 rounded-lg border text-xs',
    'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100',
    'placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent',
  );
  const labelCls = cn(secondaryText, 'text-[10px] uppercase tracking-wide mb-1', semiBoldText);

  const crumbs: { key: Step; label: string | null; fallback: string }[] = [
    { key: 'client', label: user ? (user.name || user.email) : null, fallback: t('adminTesting.addPlatform.client') },
    { key: 'restaurant', label: restaurant?.name ?? null, fallback: t('adminTesting.addPlatform.restaurant') },
    { key: 'section', label: section?.name ?? null, fallback: t('adminTesting.addPlatform.section') },
    {
      key: 'platform',
      label: existingPlatform?.platformName ?? (platformChoice === NEW_PLATFORM ? (newPlatformName || '+') : null),
      fallback: t('adminTesting.addPlatform.platform'),
    },
  ];

  const platformPreview = (p: SetupPlatform) => {
    const emailsEmpty = !p.orderEmails || p.orderEmails.length === 0;
    const namesEmpty = !p.orderClientNames || p.orderClientNames.length === 0;
    return (
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className={cn(primaryText, smallTextSize, semiBoldText)}>{p.platformName}</span>
          {p.platformName === initialPlatform && (
            <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 whitespace-nowrap">
              {t('adminTesting.addPlatform.matchesEmail')}
            </span>
          )}
          {(emailsEmpty || namesEmpty) && (
            <span className="inline-flex items-center gap-1 text-[10px] text-amber-600 dark:text-amber-400 whitespace-nowrap">
              <AlertTriangle className="w-3 h-3" />
              {t('adminTesting.addPlatform.emptyFields')}
            </span>
          )}
        </div>
        <div className={cn(secondaryText, 'text-[11px] truncate mt-0.5')}>
          {emailsEmpty ? t('adminTesting.addPlatform.noEmailsYet') : (p.orderEmails ?? []).join(', ')}
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/50 p-4" onClick={onClose}>
      <div className={cn(cardClass, 'w-full max-w-xl p-6 space-y-4 max-h-[85vh] overflow-y-auto')} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className={cn(primaryText, semiBoldText)}>{t('adminTesting.addPlatform.title')}</h3>
            <p className={cn(secondaryText, 'text-xs mt-0.5')}>
              {initialPlatform || initialOrderEmail
                ? t('adminTesting.addPlatform.subtitleFromEmail', {
                    platform: initialPlatform || '?',
                    email: initialOrderEmail || '?',
                  })
                : t('adminTesting.addPlatform.subtitle')}
            </p>
          </div>
          <button onClick={onClose} className={cn(secondaryText, 'hover:opacity-70')} aria-label="Close">✕</button>
        </div>

        {/* Breadcrumb */}
        <div className="flex items-center gap-1 flex-wrap">
          {crumbs.map((c, i) => (
            <span key={c.key} className="flex items-center gap-1">
              {i > 0 && <ChevronRight className="w-3 h-3 text-gray-300 dark:text-gray-600" />}
              {c.label ? (
                <button
                  disabled={creatingSection}
                  onClick={() => goBackTo(c.key)}
                  title={t('adminTesting.addPlatform.crumbChange', { step: c.fallback })}
                  className={cn(
                    'px-2 py-1 rounded-full text-[11px] font-medium max-w-[140px] truncate transition-colors',
                    'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300',
                    'hover:bg-purple-200 dark:hover:bg-purple-900/60',
                  )}
                >
                  {c.label}
                </button>
              ) : (
                <span
                  className={cn(
                    'px-2 py-1 rounded-full text-[11px]',
                    step === c.key
                      ? cn('border border-purple-400 dark:border-purple-600 text-purple-600 dark:text-purple-300', semiBoldText)
                      : 'text-gray-400 dark:text-gray-500',
                  )}
                >
                  {c.fallback}
                </span>
              )}
            </span>
          ))}
        </div>

        {/* Step body */}
        <div className="min-h-[220px]">
          {step === 'client' && (
            users === null ? (
              <div className={cn(secondaryText, 'text-xs py-8 text-center')}>{t('adminTesting.addPlatform.loading')}</div>
            ) : (
              <div className="space-y-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 dark:text-textQuaternary-dark" />
                  <input
                    autoFocus
                    type="text"
                    value={userQuery}
                    onChange={e => setUserQuery(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && filteredUsers.length > 0) selectUser(filteredUsers[0].id); }}
                    placeholder={t('adminTesting.addPlatform.searchClients')}
                    className={cn(inputCls, 'pl-9')}
                  />
                </div>
                <div className="space-y-1 max-h-64 overflow-y-auto pr-1">
                  {filteredUsers.length === 0 ? (
                    <div className={cn(secondaryText, 'text-xs py-6 text-center')}>{t('adminTesting.addPlatform.noClientMatches')}</div>
                  ) : (
                    filteredUsers.map(u => (
                      <button key={u.id} onClick={() => selectUser(u.id)} className={rowCls}>
                        <span className={cn(
                          'w-7 h-7 rounded-full flex items-center justify-center shrink-0',
                          'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 text-xs', semiBoldText,
                        )}>
                          {(u.name || u.email)[0].toUpperCase()}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className={cn(primaryText, smallTextSize, semiBoldText, 'block truncate')}>{u.name || u.email}</span>
                          <span className={cn(secondaryText, 'text-[11px] block truncate')}>{u.email}</span>
                        </span>
                        <ChevronRight className="w-4 h-4 text-gray-300 dark:text-gray-600 shrink-0" />
                      </button>
                    ))
                  )}
                </div>
              </div>
            )
          )}

          {step === 'restaurant' && (
            loadingProfile ? (
              <div className={cn(secondaryText, 'text-xs py-8 text-center')}>{t('adminTesting.addPlatform.loading')}</div>
            ) : !profile || profile.restaurants.length === 0 ? (
              <div className={cn(secondaryText, 'text-xs py-8 text-center italic')}>{t('adminTesting.addPlatform.noRestaurants')}</div>
            ) : (
              <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
                {profile.restaurants.map(r => (
                  <button key={r.id} onClick={() => selectRestaurant(r.id)} className={rowCls}>
                    <span className="min-w-0 flex-1">
                      <span className={cn(primaryText, smallTextSize, semiBoldText, 'block truncate')}>{r.name}</span>
                      <span className={cn(secondaryText, 'text-[11px]')}>
                        {t('adminTesting.addPlatform.restaurantMeta', {
                          sections: r.sections.length,
                          platforms: r.sections.reduce((n, s) => n + s.platforms.length, 0),
                        })}
                      </span>
                    </span>
                    <ChevronRight className="w-4 h-4 text-gray-300 dark:text-gray-600 shrink-0" />
                  </button>
                ))}
              </div>
            )
          )}

          {step === 'section' && restaurant && (
            <div className="space-y-4">
            {restaurant.sections.length === 0 ? (
              <div className={cn(secondaryText, 'text-xs py-8 text-center italic')}>{t('adminTesting.addPlatform.noSections')}</div>
            ) : (
              <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
                {restaurant.sections.map(s => {
                  const hasMatch = !!initialPlatform && s.platforms.some(p => p.platformName === initialPlatform);
                  return (
                    <button key={s.id} disabled={creatingSection} onClick={() => selectSection(s.id)} className={rowCls}>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2">
                          <span className={cn(primaryText, smallTextSize, semiBoldText, 'truncate')}>{s.name}</span>
                          {hasMatch && (
                            <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 whitespace-nowrap">
                              {t('adminTesting.addPlatform.sectionHasPlatform', { platform: initialPlatform })}
                            </span>
                          )}
                        </span>
                        <span className={cn(secondaryText, 'text-[11px] uppercase tracking-wide')}>
                          {s.restaurantSectionType} · {t('adminTesting.addPlatform.sectionMeta', { count: s.platforms.length })}
                        </span>
                      </span>
                      <ChevronRight className="w-4 h-4 text-gray-300 dark:text-gray-600 shrink-0" />
                    </button>
                  );
                })}
              </div>
            )}
            <div className="space-y-2 border-t border-gray-200 dark:border-gray-700 pt-3">
              <p className={cn(primaryText, smallTextSize, semiBoldText)}>{t('adminTesting.addPlatform.createSection')}</p>
              <div className="grid gap-2 sm:grid-cols-2">
                <label className={cn(secondaryText, 'text-xs')}>
                  {t('adminTesting.addPlatform.sectionType')}
                  <select className={cn(inputCls, 'mt-1 min-h-[44px]')} value={newSectionType} disabled={creatingSection} onChange={event => setNewSectionType(event.target.value as typeof newSectionType)}>
                    {(['FOOD', 'DRINKS', 'NON_FOOD'] as const).map(type => <option value={type} key={type}>{t(`accountSetup.suppliers.sectionType.${type}`)}</option>)}
                  </select>
                </label>
                <label className={cn(secondaryText, 'text-xs')}>
                  {t('adminTesting.addPlatform.sectionName')}
                  <input className={cn(inputCls, 'mt-1 min-h-[44px]')} value={newSectionName} disabled={creatingSection} placeholder={t(`accountSetup.suppliers.sectionType.${newSectionType}`)} onChange={event => setNewSectionName(event.target.value)} />
                </label>
              </div>
              <button className={cn(rowCls, 'min-h-[44px] justify-center disabled:opacity-50')} disabled={creatingSection} onClick={() => void addSection()}>
                <Plus className="h-4 w-4" />{t(creatingSection ? 'adminTesting.addPlatform.saving' : 'adminTesting.addPlatform.createSection')}
              </button>
            </div>
            </div>
          )}

          {step === 'platform' && section && (
            <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
              <p className={cn(secondaryText, 'text-xs mb-2')}>{t('adminTesting.addPlatform.pickPlatformHint')}</p>
              {section.platforms.map(p => (
                <button key={p.id} onClick={() => { setPlatformChoice(p.id); setDone(false); }} className={rowCls}>
                  {platformPreview(p)}
                  <ChevronRight className="w-4 h-4 text-gray-300 dark:text-gray-600 shrink-0" />
                </button>
              ))}
              <button
                onClick={() => { setPlatformChoice(NEW_PLATFORM); setDone(false); }}
                className={cn(rowCls, 'border-dashed')}
              >
                <span className={cn(
                  'w-7 h-7 rounded-full flex items-center justify-center shrink-0',
                  'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
                )}>
                  <Plus className="w-4 h-4" />
                </span>
                <span className={cn(primaryText, smallTextSize, semiBoldText)}>
                  {t('adminTesting.addPlatform.newPlatform')}
                  {initialPlatform && PLATFORM_OPTIONS.includes(initialPlatform) && !section.platforms.some(p => p.platformName === initialPlatform) && (
                    <span className={cn(secondaryText, 'font-normal')}> — {initialPlatform}</span>
                  )}
                </span>
              </button>
            </div>
          )}

          {step === 'edit' && section && restaurant && (
            <div className="space-y-4">
              {/* Context banner */}
              <div className={cn(
                'px-3 py-2 rounded-lg text-xs',
                'bg-purple-50 dark:bg-purple-900/15 text-purple-700 dark:text-purple-300',
              )}>
                {platformChoice === NEW_PLATFORM
                  ? t('adminTesting.addPlatform.creatingUnder', { section: section.name, restaurant: restaurant.name })
                  : t('adminTesting.addPlatform.updatingUnder', { platform: existingPlatform?.platformName, section: section.name, restaurant: restaurant.name })}
              </div>

              {platformChoice === NEW_PLATFORM && (
                <div>
                  <div className={labelCls}>{t('adminTesting.addPlatform.platform')}</div>
                  <select value={newPlatformName} onChange={e => setNewPlatformName(e.target.value)} className={inputCls}>
                    <option value="">{t('adminTesting.addPlatform.enumPlaceholder')}</option>
                    {PLATFORM_OPTIONS
                      .filter(p => !section.platforms.some(sp => sp.platformName === p))
                      .map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              )}

              <div>
                <div className={labelCls}>{t('adminTesting.addPlatform.orderEmails')}</div>
                <ChipListEditor values={orderEmails} onChange={setOrderEmails} placeholder="orders@supplier.nl" />
              </div>
              <div>
                <div className={labelCls}>{t('adminTesting.addPlatform.clientNames')}</div>
                <ChipListEditor values={orderClientNames} onChange={setOrderClientNames} placeholder={t('adminTesting.addPlatform.clientNamePlaceholder')} />
              </div>
            </div>
          )}
        </div>

        {error && <div className="text-xs text-red-600 dark:text-red-400">{error}</div>}
        {done && (
          <div className={cn(
            'flex items-center gap-2 px-3 py-2 rounded-lg text-xs',
            'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300',
          )}>
            <Check className="w-4 h-4 shrink-0" />
            {t('adminTesting.addPlatform.saved')}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 pt-1">
          <button onClick={onClose} className={cn('px-3 py-2 rounded-lg', secondaryText, smallTextSize, 'hover:bg-gray-100 dark:hover:bg-gray-700')}>
            {t('adminTesting.addPlatform.close')}
          </button>
          {step === 'edit' && (
            <button
              onClick={save}
              disabled={!canSave}
              className={cn('px-4 py-2 rounded-lg bg-purple-600 dark:bg-purple-600 text-white dark:text-white hover:bg-purple-700 dark:hover:bg-purple-700 disabled:opacity-40 disabled:cursor-not-allowed', smallTextSize, semiBoldText)}
            >
              {saving ? t('adminTesting.addPlatform.saving') : t('adminTesting.addPlatform.save')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
